import React, { Component } from "react";
import { Button, Modal, Result, Spin, Typography } from "antd";
import { withNamespaces } from "react-i18next";

import instance from "../../store/actions/instance";

const { Text } = Typography;

class HybridTerminalPaymentModal extends Component {
  state = {
    status: "sending",
    message: "",
    paymentDetails: null,
  };

  pollTimer = null;
  unmounted = false;

  componentDidMount() {
    this.sendPayment(false);
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.clearPollTimer();
  }

  clearPollTimer = () => {
    if (this.pollTimer) {
      window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  };

  scheduleStatusCheck = () => {
    this.clearPollTimer();
    this.pollTimer = window.setTimeout(this.checkStatus, 3000);
  };

  sendPayment = async (retry) => {
    this.clearPollTimer();
    this.setState({
      status: retry ? "resending" : "sending",
      message: "",
    });
    try {
      const res = await instance.post("/send_hybrid_pos_terminal_payment/", {
        ...(this.props.isCheckout
          ? { checkout_id: this.props.order.id }
          : { order_id: this.props.order.id }),
        terminal_id: this.props.terminal.id,
        retry,
      });
      if (this.unmounted) return;
      if (res.data.status === "paid") {
        this.setState({ status: "paid", paymentDetails: res.data });
        return;
      }
      this.setState({ status: "pending" });
      this.scheduleStatusCheck();
    } catch (err) {
      if (this.unmounted) return;
      const responseStatus = err.response?.data?.status;
      this.setState({
        status:
          responseStatus === "processing_error"
            ? "processing_error"
            : "failed",
        message:
          err.response?.data?.message ||
          this.props.t("Could not send the payment to the terminal"),
        canRetry: responseStatus !== "processing_error",
      });
    }
  };

  checkStatus = async () => {
    try {
      const res = await instance.get(
        this.props.isCheckout
          ? `/hybrid_pos_terminal_checkout_status/${this.props.order.id}/`
          : `/hybrid_pos_terminal_payment_status/${this.props.order.id}/`,
      );
      if (this.unmounted) return;
      if (res.data.status === "paid") {
        this.setState({ status: "paid", paymentDetails: res.data });
      } else if (
        res.data.status === "failed" ||
        res.data.status === "processing_error"
      ) {
        this.setState({
          status: res.data.status,
          message:
            res.data.message ||
            (res.data.status === "processing_error"
              ? this.props.t(
                  "Payment was received, but the order could not be created",
                )
              : this.props.t("The terminal payment failed")),
          canRetry: res.data.can_retry !== false,
        });
      } else {
        this.setState({ status: "pending" });
        this.scheduleStatusCheck();
      }
    } catch (err) {
      if (this.unmounted) return;
      this.setState({ status: "pending" });
      this.scheduleStatusCheck();
    }
  };

  finish = () => {
    const paymentDetails = this.state.paymentDetails || {};
    const paidOrder =
      paymentDetails.order || paymentDetails.checkout?.order;
    if (paidOrder) {
      this.props.onPaid(paidOrder);
      return;
    }
    this.props.onPaid({
      ...this.props.order,
      payment_complete: true,
      payment_method:
        paymentDetails.payment_method || this.props.order.payment_method,
      payment_reference:
        paymentDetails.payment_reference ||
        this.props.order.payment_reference,
    });
  };

  render() {
    const { t, terminal, order, visible } = this.props;
    const { status, message, canRetry = true } = this.state;
    const waiting = ["sending", "resending", "pending"].includes(status);
    const hasError = ["failed", "processing_error"].includes(status);
    const checkoutMustStayOpen =
      this.props.isCheckout && status !== "paid";

    return (
      <Modal
        visible={visible}
        title={t("Terminal payment")}
        closable={hasError && !checkoutMustStayOpen}
        maskClosable={false}
        keyboard={!checkoutMustStayOpen}
        onCancel={
          checkoutMustStayOpen ? undefined : this.props.onClose
        }
        footer={
          waiting && !checkoutMustStayOpen
            ? [
                <Button key="close" onClick={this.props.onClose}>
                  {t("Close")}
                </Button>,
              ]
            : null
        }
      >
        {waiting && (
          <div className="text-center" style={{ padding: "36px 10px" }}>
            <Spin size="large" />
            <p style={{ fontSize: 17, fontWeight: 600, marginTop: 22 }}>
              {status === "sending"
                ? t("Sending payment to terminal")
                : status === "resending"
                  ? t("Resending payment to terminal")
                  : t("Waiting for terminal payment")}
            </p>
            <Text type="secondary">
              {terminal.name} · {order.tracking_id}
            </Text>
          </div>
        )}

        {hasError && (
          <Result
            status="error"
            title={
              status === "processing_error"
                ? t("Order creation needs attention")
                : t("Payment failed")
            }
            subTitle={message}
            extra={[
              status === "processing_error" ? (
                <Button
                  key="retry-order"
                  type="primary"
                  onClick={this.checkStatus}
                >
                  {t("Retry order creation")}
                </Button>
              ) : canRetry ? (
                <Button
                  key="retry"
                  type="primary"
                  onClick={() => this.sendPayment(true)}
                >
                  {t("Resend to terminal")}
                </Button>
              ) : null,
              !checkoutMustStayOpen ? (
                <Button key="close" onClick={this.props.onClose}>
                  {t("Close")}
                </Button>
              ) : null,
            ]}
          />
        )}

        {status === "paid" && (
          <Result
            status="success"
            title={t("Payment received")}
            subTitle={`${terminal.name} · ${order.tracking_id}`}
            extra={[
              <Button key="done" type="primary" onClick={this.finish}>
                {t("Done")}
              </Button>,
            ]}
          />
        )}
      </Modal>
    );
  }
}

export default withNamespaces()(HybridTerminalPaymentModal);
